// web/src/components/MatchCard.tsx
// Match-list card used on Dashboard ("top story") and KidDetail (matches
// list). Same compositional language as CoverHero but smaller and
// click-through. Two variants:
//
//   - 'featured': big, hero photo background, magazine cover styling.
//   - 'compact':  smaller row, photo as a leading thumbnail or coloured
//                 swatch + meta on the right. Used for the rest of the list.
//
// Tap-target: entire card. Card uses an inner Link for accessibility.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CoverLine } from './CoverLine';
import { SubDeck } from './SubDeck';
import type { Match } from '../types';
import { coverLineFor, subDeckFor, swatchColorVar } from '../utils/cover-line';

export interface MatchCardProps {
  match: Match;
  /** Pre-resolved photo URL — caller fetches via /photo-url when imagePath set. */
  photoUrl?: string | null;
  /** Optional override of the generated cover line. */
  coverLineOverride?: string;
  /** Visual size variant. */
  variant?: 'featured' | 'compact';
  /** Link target — if absent, renders non-interactive. */
  to?: string;
  /** Optional decorations rendered above the card body (e.g. Sticker chips). */
  decorations?: ReactNode;
}

function formatDateShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export function MatchCard({
  match,
  photoUrl,
  coverLineOverride,
  variant = 'compact',
  to,
  decorations,
}: MatchCardProps) {
  const cl = coverLineFor(match);
  const text = coverLineOverride ?? cl.text;
  const sub = subDeckFor(match);

  const inner =
    variant === 'featured'
      ? (
        <FeaturedCard match={match} photoUrl={photoUrl} coverLineText={text}
          swatchVar={swatchColorVar(cl.swatch)} subDeckItems={sub}
          decorations={decorations} />
      )
      : (
        <CompactCard match={match} photoUrl={photoUrl} coverLineText={text}
          swatchVar={swatchColorVar(cl.swatch)} />
      );

  if (!to) return <div>{inner}</div>;
  return (
    <Link
      to={to}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {inner}
    </Link>
  );
}

// ─── Featured (big, hero photo) ───────────────────────────────────────────

function FeaturedCard({
  match, photoUrl, coverLineText, swatchVar, subDeckItems, decorations,
}: {
  match: Match;
  photoUrl?: string | null;
  coverLineText: string;
  swatchVar: string;
  subDeckItems: string[];
  decorations?: ReactNode;
}) {
  const hasPhoto = !!photoUrl;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'relative',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        minHeight: 280,
        background: hasPhoto ? '#000' : `var(${swatchVar})`,
        color: 'var(--ink-on-green)',
        boxShadow: 'var(--shadow-2)',
      }}
    >
      {hasPhoto ? (
        <img
          src={photoUrl}
          alt=""
          loading="lazy"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : null}
      {hasPhoto ? (
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.0) 35%, rgba(0,0,0,0.65) 100%)',
          }}
        />
      ) : null}

      <div
        style={{
          position: 'relative',
          padding: 'var(--space-lg)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 'inherit',
          gap: 'var(--space-base)',
        }}
      >
        {/* top — sub-deck */}
        {subDeckItems.length > 0 ? (
          <SubDeck items={subDeckItems} onDark prefix="" />
        ) : <span />}

        {/* optional sticker row */}
        {decorations ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
            {decorations}
          </div>
        ) : null}

        {/* bottom — cover line + meta */}
        <div>
          <CoverLine text={coverLineText} size="display" align="left" />
          <div
            style={{
              marginTop: 'var(--space-sm)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--small)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(250,245,236,0.85)',
            }}
          >
            {match.opposition ?? 'Unknown opposition'}
            {typeof match.ourScore === 'number' && typeof match.theirScore === 'number'
              ? ` · ${match.ourScore}–${match.theirScore}`
              : ''}
            {match.date ? ` · ${formatDateShort(match.date)}` : ''}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

// ─── Compact (row with leading swatch / thumb) ────────────────────────────

function CompactCard({
  match, photoUrl, coverLineText, swatchVar,
}: {
  match: Match;
  photoUrl?: string | null;
  coverLineText: string;
  swatchVar: string;
}) {
  const hasPhoto = !!photoUrl;
  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        background: 'var(--surface-1)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      {/* Leading 96px swatch / thumb */}
      <div
        style={{
          flexShrink: 0,
          width: 96,
          background: hasPhoto ? '#000' : `var(${swatchVar})`,
          position: 'relative',
        }}
      >
        {hasPhoto ? (
          <img
            src={photoUrl}
            alt=""
            loading="lazy"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : null}
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: 'var(--space-base)', minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            fontSize: '1.25rem',
            lineHeight: 1.05,
            color: 'var(--green)',
            textTransform: 'uppercase',
            letterSpacing: '-0.01em',
            wordBreak: 'break-word',
          }}
        >
          {coverLineText}
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--small)',
            color: 'var(--muted)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {match.opposition ?? 'Unknown opposition'}
          {match.date ? ` · ${formatDateShort(match.date)}` : ''}
        </div>
      </div>

      {/* Trailing score */}
      {typeof match.ourScore === 'number' && typeof match.theirScore === 'number' ? (
        <div
          className="mfj-tnum"
          style={{
            flexShrink: 0,
            padding: 'var(--space-base) var(--space-lg)',
            alignSelf: 'center',
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            fontSize: '1.75rem',
            color: 'var(--green)',
          }}
        >
          {match.ourScore}–{match.theirScore}
        </div>
      ) : null}
    </motion.article>
  );
}
